import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import moment from "moment";
import "moment-timezone";
import axios from "../config/axios";
import { useConfig } from "./useConfig";
import useGlobalStore from "../store/global.store";
import useBeaconControl from "./useBeaconControl";

const TZ = "America/New_York";
const WAKE_CMD_60 = 60_000; // 60 seconds before show
const WAKE_CMD_45 = 45_000; // NEW: 45s — lower bound of the first-wake window
const WAKE_CMD_30 = 30_000; // 30 seconds before show
const WAKE_CMD_20 = 20_000; // lower bound of the pre-show sequence window
const WAKEUP_OVERLAY_DURATION = 10; // seconds — doors window length (was 15)
const AUDIO_SYNC_THRESHOLD_S = 1.5; // resync if drift exceeds this

// ---- Doors window (entry animation) ----
const DOORS_DURATION_MS = WAKEUP_OVERLAY_DURATION * 1000; // 10s
const DOORS_WAKE_INTERVAL_MS = 2000; // FAST keep-alive cadence inside the window

// ---- Handoff coordinator ----
const HANDOFF_WAKE_INTERVAL_MS = 6000; // keep-alive cadence (both modes)
const HANDOFF_FIRST_WAKE_DELAY_MS = 2000; // delay before first FAST (both modes)
const HANDOFF_WAKE_TO_CANCEL_GAP_MS = 2000; // no wake within 2s of a scheduled CANCEL
const HANDOFF_CANCEL_TO_CUE_GAP_MS = 2000; // CANCEL fires 2s before the next cue
const HANDOFF_MAX_DURATION_MS = 180_000; // 3-min runaway backstop, from coordinator start

function parseCsvTime(t) {
  const [m, s] = String(t || "")
    .trim()
    .split(":")
    .map(Number);
  if (!Number.isFinite(m) || !Number.isFinite(s)) return NaN;
  return (m * 60 + s) * 1000;
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/);
  return lines
    .slice(1)
    .map((line) => {
      const cols = line.split(",");
      const t = cols[0]?.trim();
      const fx = cols[1]?.trim();
      const vib = cols[2]?.trim() || "OFF";
      if (!t || !fx) return null;
      const offsetMs = parseCsvTime(t);
      if (isNaN(offsetMs)) return null;
      return { offsetMs, fx, vib };
    })
    .filter(Boolean)
    .sort((a, b) => a.offsetMs - b.offsetMs);
}

// Raw BLE transport — do NOT call these directly from inside the hook.
// Internal callers use the gated wrappers (gatedSendBeaconCmd /
// gatedSendCancel) defined inside the hook body, which add an isController
// check on top. Keeping these module-level so the wrappers can call them
// without circular function definitions.
function sendBeaconCmd(deviceId, fx, vib) {
  return axios
    .post("/esp/beacon-show", { deviceId, fx, vib }, { withCredentials: true })
    .catch((err) => {
      console.error("[useBeaconShow] Command failed:", { fx, vib }, err);
    });
}

function sendCancel(deviceId) {
  return axios
    .post("/esp/cancel", { deviceId }, { withCredentials: true })
    .catch((err) => {
      console.error("[useBeaconShow] Cancel failed:", err);
    });
}

/**
 * Drives the Magic Band light show for VIP Door 2 views.
 *
 * Wake/handoff lifecycle (see rooftop-wake-handoff-spec):
 *   1. Doors window (on mount, 10s): CANCEL → FAST every 2s → CANCEL.
 *   2. If doors close BEFORE showtime → pre-show one-shots fire as their
 *      windows allow, then the "showtime" coordinator starts at showtime.
 *      If doors close AT/AFTER showtime → "late" coordinator starts now.
 *   3. Coordinator (6s FAST loop) dynamically schedules a final CANCEL 2s
 *      before the next CSV cue, then hands off to the firing loop.
 *
 * Returns:
 *   streamTime    — latest stream wall-clock data (for debug panels)
 *   wakeUpOverlay — { show, secondsLeft } (graphic retired; show always false)
 *   dismissWakeUp — dormant dismiss handler (kept for view API compatibility)
 *   exitShow      — call from any VIP view's exit button
 *   debug         — diagnostics for the debug overlay
 */
export function useBeaconShow() {
  const { config } = useConfig();
  const device = useGlobalStore((state) => state.device);
  const hasBeaconAddOn = useGlobalStore((state) => state.hasBeaconAddOn);

  const [streamTime, setStreamTime] = useState(null);

  // Wake-up overlay state (graphic removed; show stays false — kept so the
  // views' <BandWakeUpOverlay> contract is unchanged).
  const [wakeUpOverlay, setWakeUpOverlay] = useState({
    show: false,
    secondsLeft: WAKEUP_OVERLAY_DURATION,
  });

  const commandsRef = useRef([]);
  const firedRef = useRef(new Set());
  const initializedRef = useRef(false);
  const showStartEpochMsRef = useRef(null);
  const streamTimeRef = useRef(null);

  // Wake-up tracking refs (wall clock, not stream time)
  const sentWake60Ref = useRef(false);
  const sentWake30Ref = useRef(false);
  const wakeUpDismissedRef = useRef(false);
  const wakeUpActiveRef = useRef(false); // master gate: true during doors + coordinator
  const wakeUpCountdownRef = useRef(null);

  // NEW: doors + coordinator refs
  const doorsRanRef = useRef(false); // doors window has run for this showtime
  const doorsDoneRef = useRef(false); // doors window has finished (gates Mode A)
  const coordinatorStartedRef = useRef(false); // one-shot: a coordinator has already started this session
  const doorsTimersRef = useRef([]); // teardown fns for doors timers
  const beaconDeviceIdRef = useRef(null); // stable read for long-lived timer callbacks

  const handoffRef = useRef({
    active: false,
    mode: null, // "showtime" | "late"
    startedAt: 0,
    wakeCount: 0,
    lastWakeAt: 0,
    cancelScheduledFor: 0, // wall-clock ms of pending CANCEL (0 = none)
    wakeIntervalId: null,
    cancelTimerId: null,
    firstWakeTimerId: null,
  });

  const beaconDeviceId = device?.magic_beacon_device?._id ?? null;

  // ===== Multi-device beacon control gate =====
  //
  // Every BLE fire from this hook routes through gatedSendBeaconCmd /
  // gatedSendCancel. When isController is false (because this device
  // doesn't have the Magic Beacon addon, or another device on the same
  // account currently holds the lock), the gated wrappers no-op.
  //
  // Ref pattern is required: the doors window, handoff coordinator, and
  // pre-show one-shot scheduler all use setTimeout/setInterval that close
  // over the captured isController. Without the ref, a user becoming
  // inactive mid-show would still see commands fire from stale closures
  // until React re-mounts the effects. The ref reads fresh on every tick.
  const { isController } = useBeaconControl();
  const isControllerRef = useRef(isController);
  useEffect(() => {
    isControllerRef.current = isController;
  }, [isController]);

  const gatedSendBeaconCmd = useCallback((deviceId, fx, vib) => {
    if (!isControllerRef.current) return Promise.resolve();
    return sendBeaconCmd(deviceId, fx, vib);
  }, []);
  const gatedSendCancel = useCallback((deviceId) => {
    if (!isControllerRef.current) return Promise.resolve();
    return sendCancel(deviceId);
  }, []);

  useEffect(() => {
    streamTimeRef.current = streamTime;
  }, [streamTime]);

  // Keep a stable device id for timer callbacks (avoids stale closures in the
  // long-lived coordinator/doors intervals).
  useEffect(() => {
    beaconDeviceIdRef.current = beaconDeviceId;
  }, [beaconDeviceId]);

  // ----------------------------------------------------------------
  // Resolve which CSV + show start time to use
  // ----------------------------------------------------------------
  const showInfo = (() => {
    if (!config) return null;

    const startTimeStr = config.vipShowOneStartTime || config.mkFWmanualVIP;
    if (!startTimeStr) return null;

    if (config.Beacon_Show_Override) {
      return { csvFile: config.Beacon_Show_Override, startTimeStr };
    }

    if (config.vipShowOneStartTime) {
      return { csvFile: "hea", startTimeStr };
    }

    const showName = String(config.showName ?? "");
    const csvFile =
      showName === "3" ? "xmas" : showName === "2" ? "halloween" : "hea";
    return { csvFile, startTimeStr };
  })();

  // ----------------------------------------------------------------
  // Coordinator helpers (declared before the effects that schedule them)
  // ----------------------------------------------------------------

  // Current stream position relative to show start, in ms. Equivalent to
  // SyncFullscreen's computeSyncShowPos(). null until both are known.
  const currentElapsedMs = () => {
    const showStart = showStartEpochMsRef.current;
    const st = streamTimeRef.current;
    if (!showStart || !st) return null;
    return st.epochMs - showStart;
  };

  // Mark all commands at/behind the current stream position as fired, so the
  // firing loop resumes at the next future cue instead of bursting misses.
  const skipPastCommands = useCallback(() => {
    const showStart = showStartEpochMsRef.current;
    const commands = commandsRef.current;
    const st = streamTimeRef.current;
    if (!showStart || !commands.length || !st) return;
    const elapsed = st.epochMs - showStart;
    commands.forEach((cmd, i) => {
      if (cmd.offsetMs <= elapsed) firedRef.current.add(i);
    });
    console.log(
      "[useBeaconShow] Skipped past commands, fired count:",
      firedRef.current.size,
    );
  }, []);

  const teardownHandoff = () => {
    const h = handoffRef.current;
    if (h.wakeIntervalId) {
      clearInterval(h.wakeIntervalId);
      h.wakeIntervalId = null;
    }
    if (h.firstWakeTimerId) {
      clearTimeout(h.firstWakeTimerId);
      h.firstWakeTimerId = null;
    }
    if (h.cancelTimerId) {
      clearTimeout(h.cancelTimerId);
      h.cancelTimerId = null;
    }
    h.active = false;
    h.mode = null;
    h.startedAt = 0;
    h.wakeCount = 0;
    h.lastWakeAt = 0;
    h.cancelScheduledFor = 0;
  };

  // Emit a keep-alive wake — self-suppresses if a CANCEL is scheduled too soon.
  const fireHandoffWake = () => {
    const h = handoffRef.current;
    if (h.cancelScheduledFor > 0) {
      const msUntilCancel = h.cancelScheduledFor - Date.now();
      if (msUntilCancel < HANDOFF_WAKE_TO_CANCEL_GAP_MS) {
        console.log(
          `[useBeaconShow] handoff: skipped wake (${msUntilCancel}ms to CANCEL)`,
        );
        return;
      }
    }
    const did = beaconDeviceIdRef.current;
    if (did) gatedSendBeaconCmd(did, "WAKE_UP_FAST", "OFF");
    h.lastWakeAt = Date.now();
    h.wakeCount += 1;
    console.log(`[useBeaconShow] handoff: wake #${h.wakeCount}`);
  };

  // Completes the handoff: stop the wake loop, fire final CANCEL, release the
  // band to the CSV firing loop (skipping anything already past).
  const fireFinalHandoffCancel = () => {
    const h = handoffRef.current;
    if (!h.active) return;
    if (h.wakeIntervalId) {
      clearInterval(h.wakeIntervalId);
      h.wakeIntervalId = null;
    }
    if (h.firstWakeTimerId) {
      clearTimeout(h.firstWakeTimerId);
      h.firstWakeTimerId = null;
    }
    const did = beaconDeviceIdRef.current;
    if (did) gatedSendCancel(did);
    h.active = false;
    h.cancelTimerId = null;
    h.cancelScheduledFor = 0;
    skipPastCommands();
    wakeUpActiveRef.current = false; // CSV firing loop takes over
    console.log("[useBeaconShow] handoff: final CANCEL — CSV loop takes over");
  };

  // Scan the cue list for the next cue and schedule a CANCEL 2s before it,
  // respecting the wake→cancel gap. Re-run on every wake tick.
  const tryScheduleHandoffCancel = () => {
    const h = handoffRef.current;
    if (!h.active) return;
    if (h.wakeCount < 1) return; // at least one FAST before any handoff
    const commands = commandsRef.current;
    if (!commands.length) return;

    const elapsed = currentElapsedMs();
    if (elapsed == null) return;

    let targetIdx = -1;
    for (let i = 0; i < commands.length; i++) {
      const c = commands[i];
      if (c.offsetMs <= elapsed) continue; // past cue
      const msUntilCue = c.offsetMs - elapsed;
      const cancelInMs = msUntilCue - HANDOFF_CANCEL_TO_CUE_GAP_MS;
      if (cancelInMs < 0) continue; // too close to schedule a clean CANCEL
      const cancelAtWallMs = Date.now() + cancelInMs;
      if (
        h.lastWakeAt > 0 &&
        cancelAtWallMs - h.lastWakeAt < HANDOFF_WAKE_TO_CANCEL_GAP_MS
      ) {
        continue; // would violate wake→cancel gap
      }
      targetIdx = i;
      break;
    }

    if (targetIdx < 0) {
      // No schedulable cue. If the show is fully past, end the coordinator.
      const lastCueMs = commands[commands.length - 1].offsetMs;
      if (h.wakeCount >= 1 && elapsed > lastCueMs) {
        console.log("[useBeaconShow] handoff: no cues remain — final CANCEL");
        if (h.wakeIntervalId) {
          clearInterval(h.wakeIntervalId);
          h.wakeIntervalId = null;
        }
        const did = beaconDeviceIdRef.current;
        if (did) gatedSendCancel(did);
        h.active = false;
        skipPastCommands();
        wakeUpActiveRef.current = false;
      }
      return;
    }

    const target = commands[targetIdx];
    const cancelInMs =
      target.offsetMs - elapsed - HANDOFF_CANCEL_TO_CUE_GAP_MS;
    const cancelAtWallMs = Date.now() + cancelInMs;

    if (Math.abs(h.cancelScheduledFor - cancelAtWallMs) < 500) return; // idempotent

    if (h.cancelTimerId) {
      clearTimeout(h.cancelTimerId);
      h.cancelTimerId = null;
    }
    h.cancelScheduledFor = cancelAtWallMs;
    h.cancelTimerId = setTimeout(fireFinalHandoffCancel, cancelInMs);
    console.log(
      `[useBeaconShow] handoff: CANCEL scheduled in ${cancelInMs}ms (next cue ${(
        target.offsetMs / 1000
      ).toFixed(1)}s: ${target.fx})`,
    );
  };

  const handoffWakeTick = () => {
    const h = handoffRef.current;
    if (!h.active) return;
    if (Date.now() - h.startedAt > HANDOFF_MAX_DURATION_MS) {
      console.warn("[useBeaconShow] handoff: 3-min backstop — giving up");
      if (h.wakeIntervalId) {
        clearInterval(h.wakeIntervalId);
        h.wakeIntervalId = null;
      }
      const did = beaconDeviceIdRef.current;
      if (did) gatedSendCancel(did);
      h.active = false;
      skipPastCommands();
      wakeUpActiveRef.current = false;
      return;
    }
    fireHandoffWake();
    tryScheduleHandoffCancel();
  };

  // Entry point. Idempotent. Both modes wait HANDOFF_FIRST_WAKE_DELAY (2s)
  // before the first FAST; "showtime" fires a leading CANCEL first, "late"
  // relies on the doors' closing CANCEL having just cleared the band.
  const startHandoff = (mode) => {
    const h = handoffRef.current;
    if (h.active) {
      console.log(`[useBeaconShow] handoff: already active (${mode})`);
      return;
    }
    h.active = true;
    h.mode = mode;
    coordinatorStartedRef.current = true; // latch — the tick must never re-arm
    h.startedAt = Date.now();
    h.wakeCount = 0;
    h.lastWakeAt = 0;
    h.cancelScheduledFor = 0;
    wakeUpActiveRef.current = true; // gate CSV + one-shots

    console.log(`[useBeaconShow] handoff: started (${mode})`);

    const did = beaconDeviceIdRef.current;
    if (mode === "showtime" && did) {
      gatedSendCancel(did); // clear residual from the -30s sequence
    }

    h.firstWakeTimerId = setTimeout(() => {
      h.firstWakeTimerId = null;
      fireHandoffWake();
      h.wakeIntervalId = setInterval(handoffWakeTick, HANDOFF_WAKE_INTERVAL_MS);
      tryScheduleHandoffCancel();
    }, HANDOFF_FIRST_WAKE_DELAY_MS);
  };

  // ----------------------------------------------------------------
  // Load CSV whenever the show type changes
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!showInfo) return;

    fetch(`/firework/${showInfo.csvFile}.csv`)
      .then((r) => {
        if (!r.ok) throw new Error(`CSV fetch failed: ${r.status}`);
        return r.text();
      })
      .then((text) => {
        commandsRef.current = parseCsv(text);
        firedRef.current = new Set();
        initializedRef.current = false;
        console.log(
          `[useBeaconShow] Loaded ${commandsRef.current.length} commands from ${showInfo.csvFile}.csv`,
        );
      })
      .catch((err) => {
        console.error("[useBeaconShow] Failed to load CSV:", err);
      });
  }, [showInfo?.csvFile]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------------
  // Keep show start epoch in sync with config
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!showInfo?.startTimeStr) {
      showStartEpochMsRef.current = null;
      return;
    }
    const todayStr = moment().tz(TZ).format("YYYY-MM-DD");
    const epoch = moment
      .tz(`${todayStr} ${showInfo.startTimeStr}`, "YYYY-MM-DD HH:mm", TZ)
      .valueOf();
    showStartEpochMsRef.current = epoch;

    // Reset firing position so a mid-session showtime change re-evaluates.
    firedRef.current = new Set();
    initializedRef.current = false;

    // Reset wake / doors / coordinator state for the new showtime.
    sentWake60Ref.current = false;
    sentWake30Ref.current = false;
    wakeUpDismissedRef.current = false;
    wakeUpActiveRef.current = false;
    teardownHandoff();
    doorsTimersRef.current.forEach((fn) => fn());
    doorsTimersRef.current = [];
    doorsRanRef.current = false;
    doorsDoneRef.current = false;
    coordinatorStartedRef.current = false;
    setWakeUpOverlay({ show: false, secondsLeft: WAKEUP_OVERLAY_DURATION });
  }, [showInfo?.startTimeStr]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------------
  // Listen for stream wall-clock timestamps from the Clappr iframe
  // ----------------------------------------------------------------
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== "streamDisplayedTime") return;
      setStreamTime({
        epochMs: e.data.epochMs,
        delayMs: e.data.delayMs,
        isoTime: e.data.isoTime,
      });
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // ----------------------------------------------------------------
  // Doors window (entry animation) — runs once on mount per showtime.
  //
  //   t=0            CANCEL (clear sleeping band)
  //   +2,+4,+6,+8s   WAKE_UP_FAST
  //   t=10s          CANCEL (closing bookend) → decide handoff mode:
  //                    untilShow > 0 → release to pre-show one-shots; the
  //                                    "showtime" coordinator starts at showtime
  //                    untilShow <= 0 → start the "late" coordinator now
  //
  // Owns the band exclusively for its full 10s (wakeUpActiveRef = true), so
  // CSV firing and the -45/-60 + -20/-30 one-shots are all suppressed.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!beaconDeviceId) return;
    if (!hasBeaconAddOn) return;
    if (doorsRanRef.current) return;
    if (!showStartEpochMsRef.current) return; // wait until showtime resolves

    doorsRanRef.current = true;
    doorsDoneRef.current = false;
    wakeUpActiveRef.current = true; // gate everything during doors
    setWakeUpOverlay({ show: false, secondsLeft: WAKEUP_OVERLAY_DURATION });

    console.log("[useBeaconShow] doors: t=0 CANCEL");
    gatedSendCancel(beaconDeviceId); // leading cancel

    const wakeId = setInterval(() => {
      const did = beaconDeviceIdRef.current;
      if (did) {
        console.log("[useBeaconShow] doors: WAKE_UP_FAST");
        gatedSendBeaconCmd(did, "WAKE_UP_FAST", "OFF");
      }
    }, DOORS_WAKE_INTERVAL_MS);

    const endId = setTimeout(() => {
      clearInterval(wakeId);
      const did = beaconDeviceIdRef.current;
      if (did) {
        console.log("[useBeaconShow] doors: t=10s closing CANCEL");
        gatedSendCancel(did); // closing bookend
      }
      doorsDoneRef.current = true;

      const showStart = showStartEpochMsRef.current;
      const untilShow = showStart ? showStart - Date.now() : 1;
      if (untilShow > 0) {
        // Pre-show: release the band; one-shots fire, Mode A starts at showtime.
        wakeUpActiveRef.current = false;
        console.log("[useBeaconShow] doors: closed pre-show — awaiting showtime");
      } else {
        // At/after showtime: doors' closing CANCEL just cleared the band, so
        // the "late" coordinator waits 2s (breathing room) then wakes.
        console.log("[useBeaconShow] doors: closed past showtime — late handoff");
        startHandoff("late");
      }
    }, DOORS_DURATION_MS);

    doorsTimersRef.current.push(() => clearInterval(wakeId));
    doorsTimersRef.current.push(() => clearTimeout(endId));

    return () => {
      clearInterval(wakeId);
      clearTimeout(endId);
    };
  }, [beaconDeviceId, hasBeaconAddOn, showInfo?.startTimeStr]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------------
  // Wall-clock timer (1s) — pre-show one-shots + "showtime" coordinator start.
  //
  // All gated by !wakeUpActiveRef so nothing fires while the doors window or a
  // coordinator owns the band.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!beaconDeviceId) return;
    if (!hasBeaconAddOn) return;

    const tick = () => {
      const showStart = showStartEpochMsRef.current;
      if (!showStart) return;

      const now = Date.now();
      const untilShow = showStart - now; // ms until show start (neg = started)

      // First wake — single SLOW, fires once in the 45–60s window.
      if (
        untilShow <= WAKE_CMD_60 &&
        untilShow > WAKE_CMD_45 &&
        !sentWake60Ref.current &&
        !wakeUpActiveRef.current
      ) {
        sentWake60Ref.current = true;
        console.log("[useBeaconShow] one-shot: SLOW at -45..-60s");
        gatedSendBeaconCmd(beaconDeviceId, "WAKE_UP_SLOW", "OFF");
      }

      // Pre-show sequence — fires once in the 20–30s window:
      //   t=0 CANCEL → +2s SLOW → +8s FAST → +14s FAST
      if (
        untilShow <= WAKE_CMD_30 &&
        untilShow >= WAKE_CMD_20 &&
        !sentWake30Ref.current &&
        !wakeUpActiveRef.current
      ) {
        sentWake30Ref.current = true;
        console.log("[useBeaconShow] one-shot: pre-show sequence at -20..-30s");
        gatedSendCancel(beaconDeviceId); // leading cancel
        setTimeout(() => {
          const did = beaconDeviceIdRef.current;
          if (did) gatedSendBeaconCmd(did, "WAKE_UP_SLOW", "OFF");
        }, 2000);
        setTimeout(() => {
          const did = beaconDeviceIdRef.current;
          if (did) gatedSendBeaconCmd(did, "WAKE_UP_FAST", "OFF");
        }, 8000);
        setTimeout(() => {
          const did = beaconDeviceIdRef.current;
          if (did) gatedSendBeaconCmd(did, "WAKE_UP_FAST", "OFF");
        }, 14000);
      }

      // "showtime" coordinator — starts once when showtime crosses, but only
      // if doors finished pre-show (doorsDoneRef) and nothing else owns the
      // band. A late user (doors finished past showtime) already started the
      // "late" coordinator, so h.active / wakeUpActiveRef block this.
      if (
        untilShow <= 0 &&
        doorsDoneRef.current &&
        !wakeUpActiveRef.current &&
        !handoffRef.current.active &&
        !coordinatorStartedRef.current
      ) {
        console.log("[useBeaconShow] showtime crossed — starting coordinator");
        startHandoff("showtime");
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [beaconDeviceId, hasBeaconAddOn]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------------
  // Dismiss handler — dormant (overlay graphic retired) but kept so the views'
  // <BandWakeUpOverlay onDismiss={...}> prop contract is unchanged.
  // ----------------------------------------------------------------
  const dismissWakeUp = useCallback(() => {
    wakeUpDismissedRef.current = true;
    teardownHandoff();
    doorsTimersRef.current.forEach((fn) => fn());
    doorsTimersRef.current = [];
    wakeUpActiveRef.current = false;
    if (wakeUpCountdownRef.current) {
      clearInterval(wakeUpCountdownRef.current);
      wakeUpCountdownRef.current = null;
    }
    skipPastCommands();
    setWakeUpOverlay({ show: false, secondsLeft: 0 });
    if (beaconDeviceId) {
      console.log("[useBeaconShow] dismissed — sending cancel");
      gatedSendCancel(beaconDeviceId);
    }
  }, [beaconDeviceId, skipPastCommands]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------------
  // Exit show — call from any VIP view's exit button.
  // Tears down the coordinator + doors timers, then CANCEL → 1s → SOLID_OFF.
  // ----------------------------------------------------------------
  const exitShow = useCallback(() => {
    teardownHandoff();
    doorsTimersRef.current.forEach((fn) => fn());
    doorsTimersRef.current = [];
    if (wakeUpCountdownRef.current) {
      clearInterval(wakeUpCountdownRef.current);
      wakeUpCountdownRef.current = null;
    }
    wakeUpActiveRef.current = false;
    setWakeUpOverlay({ show: false, secondsLeft: 0 });
    if (beaconDeviceId) {
      gatedSendCancel(beaconDeviceId).then(() => {
        setTimeout(() => {
          gatedSendBeaconCmd(beaconDeviceId, "SOLID_OFF", "OFF");
        }, 1000);
      });
    }
  }, [beaconDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------------
  // Fire beacon commands as stream time advances (the "cue runner").
  // Gated by wakeUpActiveRef so it stays silent during doors + coordinator;
  // on handoff it skips everything already past and resumes at the next cue.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!streamTime) return;
    if (!beaconDeviceId) return;
    if (!hasBeaconAddOn) return;
    if (wakeUpActiveRef.current) return;

    const showStartEpochMs = showStartEpochMsRef.current;
    if (!showStartEpochMs) return;

    const commands = commandsRef.current;
    if (!commands.length) return;

    const elapsed = streamTime.epochMs - showStartEpochMs;
    if (elapsed < 0) return;

    if (!initializedRef.current) {
      initializedRef.current = true;
      commands.forEach((cmd, i) => {
        if (cmd.offsetMs <= elapsed) firedRef.current.add(i);
      });
      const skipped = firedRef.current.size;
      if (skipped > 0) {
        console.log(`[useBeaconShow] Late entry — skipped ${skipped} past commands`);
      }
    }

    commands.forEach((cmd, i) => {
      if (!firedRef.current.has(i) && cmd.offsetMs <= elapsed) {
        firedRef.current.add(i);
        console.log(
          `[useBeaconShow] Firing +${cmd.offsetMs}ms: fx=${cmd.fx} vib=${cmd.vib}`,
        );
        gatedSendBeaconCmd(beaconDeviceId, cmd.fx, cmd.vib);
      }
    });
  }, [streamTime, beaconDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------------
  // Show audio engine — synced to stream time, independent of Settings volume
  // ----------------------------------------------------------------
  const audioRef = useRef(null);
  const audioReadyRef = useRef(false);
  const [audioState, setAudioState] = useState({
    status: "idle", // idle | loading | playing | ended | error
    currentTime: 0,
    duration: 0,
  });
  const setBeaconShowPlaying = useGlobalStore(
    (state) => state.setBeaconShowPlaying,
  );

  useEffect(() => {
    if (!showInfo?.csvFile) return;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
      audioReadyRef.current = false;
      setBeaconShowPlaying(false);
    }

    const audio = new Audio(`/firework/${showInfo.csvFile}.mp3`);
    audio.preload = "auto";
    audio.volume = 0.7;
    audioRef.current = audio;

    const onCanPlay = () => {
      audioReadyRef.current = true;
      setAudioState((s) => ({
        ...s,
        status: "loading",
        duration: audio.duration || 0,
      }));
      console.log(
        `[useBeaconShow] Audio ready: ${showInfo.csvFile}.mp3 (${Math.round(audio.duration)}s)`,
      );
    };

    const onEnded = () => {
      setAudioState((s) => ({ ...s, status: "ended" }));
      setBeaconShowPlaying(false);
      console.log("[useBeaconShow] Show audio ended");
    };

    const onError = () => {
      setAudioState((s) => ({ ...s, status: "error" }));
      setBeaconShowPlaying(false);
      console.error("[useBeaconShow] Audio load error:", showInfo.csvFile);
    };

    audio.addEventListener("canplaythrough", onCanPlay);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    setAudioState({ status: "loading", currentTime: 0, duration: 0 });

    return () => {
      audio.removeEventListener("canplaythrough", onCanPlay);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.pause();
      audio.src = "";
      audioRef.current = null;
      audioReadyRef.current = false;
      setBeaconShowPlaying(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInfo?.csvFile]);

  useEffect(() => {
    if (!streamTime) return;
    const audio = audioRef.current;
    if (!audio || !audioReadyRef.current) return;

    const showStart = showStartEpochMsRef.current;
    if (!showStart) return;

    const elapsedMs = streamTime.epochMs - showStart;
    const elapsedS = elapsedMs / 1000;
    const duration = audio.duration || 0;

    if (elapsedS < 0) {
      if (!audio.paused) {
        audio.pause();
        setBeaconShowPlaying(false);
        setAudioState((s) => ({ ...s, status: "idle" }));
      }
      return;
    }

    if (duration > 0 && elapsedS > duration) {
      if (!audio.paused) {
        audio.pause();
        setBeaconShowPlaying(false);
        setAudioState((s) => ({ ...s, status: "ended" }));
        console.log("[useBeaconShow] Audio stopped — show is over");
      }
      return;
    }

    const drift = Math.abs(audio.currentTime - elapsedS);

    if (audio.paused) {
      audio.currentTime = elapsedS;
      audio.volume = 0.7;
      audio.play().catch((err) => {
        console.warn("[useBeaconShow] Audio autoplay blocked:", err.message);
      });
      setBeaconShowPlaying(true);
      setAudioState({ status: "playing", currentTime: elapsedS, duration });
      console.log(`[useBeaconShow] Audio started at ${elapsedS.toFixed(1)}s`);
    } else if (drift > AUDIO_SYNC_THRESHOLD_S) {
      audio.currentTime = elapsedS;
      console.log(
        `[useBeaconShow] Audio resynced: drift=${drift.toFixed(1)}s → ${elapsedS.toFixed(1)}s`,
      );
    }

    if (audio.volume !== 0.7) audio.volume = 0.7;

    setAudioState({
      status: audio.paused ? "idle" : "playing",
      currentTime: audio.currentTime,
      duration,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamTime]);

  // Cleanup on unmount — store flag, coordinator timers, doors timers.
  useEffect(() => {
    return () => {
      setBeaconShowPlaying(false);
      teardownHandoff();
      doorsTimersRef.current.forEach((fn) => fn());
      doorsTimersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------
  // Debug info — recomputed on every streamTime tick (~500ms)
  // ----------------------------------------------------------------
  const debug = useMemo(() => {
    const showStart = showStartEpochMsRef.current;
    const commands = commandsRef.current;
    const fired = firedRef.current;
    const elapsed =
      streamTime && showStart ? streamTime.epochMs - showStart : null;

    let nextCmd = null;
    for (let i = 0; i < commands.length; i++) {
      if (!fired.has(i)) {
        nextCmd = { index: i, ...commands[i] };
        break;
      }
    }

    let lastCmd = null;
    for (let i = commands.length - 1; i >= 0; i--) {
      if (fired.has(i)) {
        lastCmd = { index: i, ...commands[i] };
        break;
      }
    }

    const h = handoffRef.current;

    return {
      csvFile: showInfo?.csvFile ?? null,
      startTimeStr: showInfo?.startTimeStr ?? null,
      showStartEpochMs: showStart,
      beaconDeviceId,
      commandsTotal: commands.length,
      commandsFired: fired.size,
      elapsedMs: elapsed,
      lastFired: lastCmd,
      nextCommand: nextCmd,
      wake60Sent: sentWake60Ref.current,
      wake30Sent: sentWake30Ref.current,
      wakeUpOverlayActive: wakeUpActiveRef.current,
      initialized: initializedRef.current,
      audio: audioState,
      // NEW: coordinator + doors diagnostics
      doorsDone: doorsDoneRef.current,
      handoff: {
        active: h.active,
        mode: h.mode,
        wakeCount: h.wakeCount,
        cancelInMs: h.cancelScheduledFor
          ? Math.max(0, h.cancelScheduledFor - Date.now())
          : null,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamTime, showInfo, beaconDeviceId]);

  return { streamTime, wakeUpOverlay, dismissWakeUp, exitShow, debug };
}
